import { useState } from "react";
import BasicDataTable from "../../Component/BasicComponents/BasicDataTable";
import PageHeader from "../../Component/BasicComponents/HeaderBreadCrumbs";
import RoundedDropdown from "../../Component/BasicComponents/Dropdowns/RoundedDropdown";
import {
  useLeaveBalanceData,
  useLeaveTypesData,
} from "../leave/hooks/useLeave";
import { showToast } from "../../utils/toastUtils";
import { BASE_URL } from "../../Api/config";

const LeaveBalanceColumns = [
  {
    name: "Emp Name",
    selector: (row: { FirstName: string; LastName: string }) =>
      `${row.FirstName ?? ""} ${row.LastName ?? ""}`.trim() || "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Emp Code",
    selector: (row: { EmployeeCode: string }) => row.EmployeeCode ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Leave Year",
    selector: (row: { LeaveYear: string }) => row.LeaveYear ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Leave Type",
    selector: (row: { LeaveType: string }) => row.LeaveType ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Balance",
    selector: (row: { Balance: string }) => row.Balance ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Accuration",
    selector: (row: { Accuration: string }) => row.Accuration ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Opening",
    selector: (row: { Opening: string }) => row.Opening ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Reward",
    selector: (row: { Reward: string }) => row.Reward ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
  {
    name: "Consumed",
    selector: (row: { Consumed: string }) => row.Consumed ?? "-",
    sortable: true,
    minWidth: "180px",
    wrap: true,
  },
];

const LeaveBalance: React.FC = () => {
  const generateYearOptions = (): { id: number | null; label: string }[] => {
    const currentYear = new Date().getFullYear();
    const years: { id: number | null; label: string }[] = [
      { id: null, label: "All" },
    ];

    for (let i = 0; i < 10; i++) {
      years.push({ id: currentYear - i, label: String(currentYear - i) });
    }

    return years;
  };

  const filterYearData = generateYearOptions();

  const { data: leaveTypeData } = useLeaveTypesData({ enable: true });

  console.log("🚀 ~ LeaveBalance.tsx:83 ~ leaveTypeData:", leaveTypeData?.data);

  const filterTypeData = leaveTypeData?.data?.map((item: any) => ({
    id: item?.ShortCode,
    label: item?.LeaveType,
  }));

  const [filtersState, setFiltersState] = useState<any>({
    page: 1,
    limit: 10,
    employee_id: "",
    leave_year: "",
    leave_type: "",
    search: null,
  });
  const [disabled, setDisabled] = useState(false);

  const { data: leaveBalanceData, isLoading: leaveBalanceLoading } =
    useLeaveBalanceData({
      enable: true,
      params: {
        employee_id: "",
        leave_year: filtersState?.leave_year,
        leave_type: filtersState?.leave_type,
        search: filtersState?.search,
        page: filtersState?.page,
        limit: filtersState?.limit,
      },
    });

  const handlePagination = (pageNo: number) => {
    setFiltersState((prevState: any) => {
      return {
        ...prevState,
        page: pageNo,
      };
    });
  };

  const handleonChangeRowsPerPage = (limit: any, pageNo: any) => {
    setFiltersState((prevState: any) => {
      return {
        ...prevState,
        page: pageNo,
        limit: limit,
      };
    });
  };

  const handleSearch = (searchValue: any) => {
    setFiltersState((prevState: any) => {
      return {
        ...prevState,
        search: searchValue,
      };
    });
  };

  const handleFilter = (key: any, selected: any) => {
    setFiltersState((prev: any) => ({
      ...prev,
      [key]: selected.id === "null" ? null : selected.id,
    }));
  };

  const downloadExcelLeaveBalance = async () => {
    const appToken = localStorage.getItem("Apptoken") || "";

    const params: Record<string, any> = {
      ...(filtersState.employee_id
        ? { employee_id: filtersState.employee_id }
        : {}),
      ...(filtersState.status ? { status: filtersState.status } : {}),
      ...(filtersState.search ? { search: filtersState.search } : {}),
    };

    const queryString = new URLSearchParams(params).toString();
    const baseUrl = `${BASE_URL}admin/api/getEmpLeaveBalanceExcel`;
    const apiEndpoint = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    let response: Response | null = null; // ✅ Declare it outside try block

    try {
      setDisabled(true);

      response = await fetch(apiEndpoint, {
        method: "GET",
        headers: {
          apptoken: appToken,
          accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log("Blob type:", blob.type); // Debug

      const contentDisposition = response.headers.get("content-disposition");
      const fileNameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const fileName = fileNameMatch
        ? fileNameMatch[1].endsWith(".xlsx")
          ? fileNameMatch[1]
          : `${fileNameMatch[1]}.xlsx`
        : "LeaveBalanceExcel.xlsx";

      const excelBlob = new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(excelBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Error while downloading Excel:", error);

      // ✅ Check response only if it exists
      if (response) {
        try {
          const text = await response.text();
          console.log("Server response (not Excel):", text);
        } catch {
          console.log("Unable to read response body.");
        }
      }

      showToast.error("Failed to download Excel file.");
    } finally {
      setDisabled(false);
    }
  };

  return (
    <div className="p-2">
      <PageHeader
        title="Leave Balance"
        buttons={[
          {
            label: "Excel",
            onClick: downloadExcelLeaveBalance,
            color: "#43B9B2",
            disable: disabled,
          },
        ]}
      />
      <div>
        <BasicDataTable
          Title="Leave Balance"
          columns={LeaveBalanceColumns}
          data={leaveBalanceData?.data?.data || []}
          dynamicHeightOnRowsPerPage={true}
          showSpinner={leaveBalanceLoading}
          headerContent={
            <>
              <RoundedDropdown
                options={filterYearData || []}
                onSelect={(e) => handleFilter("leave_year", e)}
                defaultLabel="All"
                width="auto"
                isShowHeading
                DropdownHeading="Leave Year"
                headingColor="#2e8dd3"
              />
              <RoundedDropdown
                options={filterTypeData || []}
                onSelect={(e) => handleFilter("leave_type", e)}
                defaultLabel="All"
                width="auto"
                isShowHeading
                DropdownHeading="Leave Type"
                headingColor="#2e8dd3"
              />
            </>
          }
          onChangePage={(pageNo: number) => {
            handlePagination(pageNo);
          }}
          onChangeRowsPerPage={(newPerPage: any, page: any) => {
            handleonChangeRowsPerPage(newPerPage, page);
          }}
          handleSearch={(val: any) => {
            handleSearch(val);
          }}
          paginationTotalRows={leaveBalanceData?.data?.recordsTotal || 0}
        />
      </div>
    </div>
  );
};
export default LeaveBalance;
